import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { DeclaracaoResponsabilidadeTecnicaPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/declaracao-responsabilidade-tecnica-pj/declaracao-responsabilidade-tecnica-pj.service';
import {
    ReportDeclaracaoResponsabilidadeTecnicaPjRequest,
    ReportDeclaracaoResponsabilidadeTecnicaPjResponse
} from '@/core/api/sicom-api/registro/prestador/relatorios/declaracao-responsabilidade-tecnica-pj/declaracao-responsabilidade-tecnica-pj.model';
import { DeclaracaoResponsabilidadeTecnicaPjJobService } from '@/core/services/declaracao-responsabilidade-tecnica-pj-job.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportDeclaracaoResponsabilidadeTecnicaPjRequest;
type Resp = ReportDeclaracaoResponsabilidadeTecnicaPjResponse;

/**
 * Relatório PDF assíncrono: Declaração de Responsabilidade Técnica PJ (prestador).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos
 * a identificação do prestador (nuCrm/sgUf via `PessoaService`). Sem filtros nem formulário.
 */
@Component({
    selector: 'app-declaracao-responsabilidade-tecnica-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, RelatorioContainer, CanDirective],
    templateUrl: './declaracao-responsabilidade-tecnica-pj.html',
    styleUrl: './declaracao-responsabilidade-tecnica-pj.scss'
})
export class ReportDeclaracaoResponsabilidadeTecnicaPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly declaracaoService = inject(DeclaracaoResponsabilidadeTecnicaPjService);
    private readonly pessoaService = inject(PessoaService);
    readonly declaracaoJobService = inject(DeclaracaoResponsabilidadeTecnicaPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.declaracaoJobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.declaracaoJobService;
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar a declaração.'
            });
            return null;
        }

        return { nuCrm, sgUf };
    }

    protected exportar(request: Req) {
        return this.declaracaoService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.limparJob();
    }
}
